<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';

	import { Button, Icon, Label, PremiumCard, Text } from '@Shelly/shelly-design-system';

	import {
		getPlanCardAction,
		type CameraCloudStoragePlanCardAction,
	} from 'src/helpers/camera_cloud_storage_helpers';
	import type {
		CameraCloudStorageActivePlan,
		CameraCloudStoragePlan,
	} from 'src/types/CameraCloudStorage';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';

	export let plan: CameraCloudStoragePlan;
	export let activePlan: CameraCloudStorageActivePlan | null = null;
	export let loading = false;

	const dispatch = createEventDispatcher<{ subscribe: void; upgrade: void }>();

	$: action = getPlanCardAction(plan, activePlan) as CameraCloudStoragePlanCardAction;
	$: isActive = action === 'active';
	$: isDisabled = action === 'disabled' || isActiveHomeShared();

	function handleClick() {
		if (action === 'subscribe') {
			dispatch('subscribe');
		} else if (action === 'upgrade') {
			dispatch('upgrade');
		}
	}
</script>

<div class="ccs-plan-wrapper" class:ccs-plan-wrapper--active={isActive}>
	<PremiumCard backgroundColor="color-1" isActive={false} class="height-full">
		{#if plan.savePercent}
			<Label
				slot="leftBadge"
				class="ccs-save-badge"
				value={$format('label_save_n_percent', {
					default: `Save ${plan.savePercent}%`,
					values: { value: plan.savePercent },
				})}
				icon="fas fa-badge-percent"
				size="s"
				noRadiusType="second"
			/>
		{/if}

		<div class="ccs-plan-content">
			<Text class="mb-s text-color-muted" styleType="tiny">
				{$format('subscription_plan_monthly', { default: 'MONTHLY' })}
			</Text>

			<div class="ccs-cameras-pill">
				<Text styleType="body-small" class="text-bold">
					{$format('camera_cloud_storage_plan_cameras_count', {
						default: `${plan.tier} cameras`,
						values: { count: plan.tier },
					})}
				</Text>
			</div>

			<Text class="mb-xs text-bold" styleType="controls-large">
				{$format('label_price_month_eur', {
					default: `${plan.priceMonthly.toFixed(2)} Euro/Month`,
					values: { price: plan.priceMonthly.toFixed(2) },
				})}
			</Text>
			<Text class="text-color-muted" styleType="body-extra-small">
				{$format('subscription_plan_build_every_month', { default: 'Billed every month' })}
			</Text>
			<Text class="mb-s text-color-muted" styleType="body-extra-small">
				{$format('subscription_plan_cancel', { default: 'Cancel anytime' })}
			</Text>

			{#if isActive}
				<div class="ccs-plan-active-state">
					<Icon class="mr-xs" icon="fas fa-circle-check text-success" />
					<Text class="text-bold text-success" styleType="controls-large">
						{$format('text_subscription_active', { default: 'Active' })}
					</Text>
				</div>
			{:else if isDisabled}
				<Button
					class="ccs-plan-button ccs-plan-button--purple"
					size="m"
					variant="secondary"
					disabled
				>
					{$format('button_subscription_subscribe', { default: 'Subscribe' })}
				</Button>
			{:else if action === 'upgrade'}
				<Button
					class="ccs-plan-button ccs-plan-button--purple"
					size="m"
					variant="primary"
					{loading}
					disabled={loading || isActiveHomeShared()}
					on:click={handleClick}
				>
					{$format('button_subscription_upgrade', { default: 'Upgrade' })}
				</Button>
			{:else}
				<Button
					class="ccs-plan-button ccs-plan-button--purple"
					size="m"
					variant="secondary"
					{loading}
					disabled={loading || isActiveHomeShared()}
					on:click={handleClick}
				>
					{$format('button_subscription_subscribe', { default: 'Subscribe' })}
				</Button>
			{/if}
		</div>
	</PremiumCard>
</div>

<style>
	.ccs-plan-wrapper {
		border-radius: 14px;
		padding: 1px;
		background: transparent;
		transition: background 0.2s ease-in-out;
	}

	.ccs-plan-wrapper--active {
		background: linear-gradient(135deg, #d56cf8 0%, #893bf2 100%);
	}

	:global(.ccs-save-badge) {
		background: linear-gradient(135deg, #d56cf8 0%, #893bf2 100%) !important;
	}

	:global(.ccs-save-badge),
	:global(.ccs-save-badge *) {
		color: #ffffff !important;
	}

	.ccs-plan-content {
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
		gap: 4px;
		padding: 8px 4px 4px;
		width: 100%;
	}

	.ccs-cameras-pill {
		background: #ffffff;
		color: #151617;
		border-radius: 999px;
		padding: 6px 16px;
		min-width: 120px;
		text-align: center;
		margin-bottom: 12px;
	}

	.ccs-cameras-pill :global(.text),
	.ccs-cameras-pill :global(p) {
		color: #151617 !important;
	}

	.ccs-plan-active-state {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 6px 16px;
		min-height: 32px;
	}

	:global(.ccs-plan-button) {
		width: 100%;
		margin-top: 4px;
	}

	:global(.ccs-plan-button--white) {
		background: #ffffff !important;
		color: #151617 !important;
		box-shadow: none !important;
	}

	:global(.ccs-plan-button--white .button-text),
	:global(.ccs-plan-button--white *) {
		color: #151617 !important;
	}

	:global(.ccs-plan-button--purple) {
		background: linear-gradient(135deg, #d56cf8 0%, #893bf2 100%) !important;
		color: #ffffff !important;
		box-shadow: none !important;
		background-image: linear-gradient(135deg, #d56cf8 0%, #893bf2 100%) !important;
	}
</style>
